import { ApplicationRef, Injectable } from '@angular/core';
import { AlertController, LoadingController, ModalController, NavController, Platform, PopoverController, ToastController } from '@ionic/angular';
import { first } from 'rxjs/internal/operators/first';
import { environment } from 'src/environments/environment';
import { LangPipe } from '../modules/lang/lang.pipe';
import { ApiService } from './api.service';
import { ApiqueryServiceProvider } from './apiquery-service';
import { AuthService } from './auth/auth.service';
import { ConsoleServiceProvider } from './console-service';
import { licenses } from './licenses';
import { NavServiceProvider } from './nav-service';
import { NotificationsServiceProvider } from './notifications-service';
import { VersionServiceProvider } from './version-service';

@Injectable({ providedIn: 'root' })
export class CoreProvider {

  public stable = false;
  public appIsStable = this.appRef.isStable.pipe(first(isStable => {
    if (isStable === true) {
      this.stable = true;
      return true;
    } else {
      return false;
    }
  }));
  public isMobile = false;
  public windowWidth = 0;
  public get user() {
    return this.auth.user || undefined;
  };
  public get isLoggedIn() { return this.auth.token!=''; };

  public shared: { status: 'idle'|'loading'|'saved'; } = {
    status: 'idle'
  };

  public isBrowser: boolean = false;

  constructor(
    navc: NavController,
    private _loading: LoadingController,
    public api: ApiService,
    public apiQuery: ApiqueryServiceProvider,
    public console: ConsoleServiceProvider,
    public version: VersionServiceProvider,
    public notifications: NotificationsServiceProvider,
    public auth: AuthService,
    public nav: NavServiceProvider,
    public navCtrl: NavController,
    public toastCtrl: ToastController,
    public alertCtrl: AlertController,
    public modalCtrl: ModalController,
    public popoverCtrl: PopoverController,
    public platform: Platform,
    public lpipe: LangPipe,
    private appRef: ApplicationRef
  ) {
    let lic = null;
    if (lic = licenses[location.hostname]) {
      environment.endpoint = `https://${lic.endpoint}`;
      (document.querySelector(':root') as any).style.setProperty('--ion-color-primary', lic.primaryColor??'red');
    }
    arsa = environment;
    if (!environment.production) arsa.core = this;

    apiQuery.init(this);
    nav.init(navc, this);
    auth.init(this);
    version.init(this);
    notifications.init(this);

    notifications.events.subscribe(res => {
    });
    this.appRef.isStable.subscribe(stable => {
      this.console.debug('App state changed to: '+(stable?'stable':'unstable'));
    });

    this.onResize();
    window.onresize = () => this.onResize();

    this.platformReady();
  }

  onResize() {
    this.windowWidth =  window.innerWidth;
    this.isMobile = (this.windowWidth<800) ? true : false;
  }

  isAuthenticated(): boolean {
    return this.isLoggedIn;
  }

  isRole(possibleroles: Array<string>): boolean {
    possibleroles.push('ROLE:PROGRAMMER');
    if (!this.isLoggedIn || !this.user) return false;

    for (let i = 0; i < possibleroles.length; i++) {
      if (possibleroles[i] == this.user.legacy_role) return true;
    }

    return false;
  }

  public isShowed(p: {requireRole?: string[], requireLogIn?: boolean, requireLogOut?: boolean}) {
    let flag: boolean = true;
    if (p.requireRole) for (let i = 0; i<p.requireRole.length; i++) {
      if (i==0) flag = false;
      if (!this.user) return false;
      if (this.user.legacy_role==p.requireRole[i]) flag = true;
    }
    if (this.user && this.user.legacy_role=='ROLE:PROGRAMMER') flag = true;

    return flag && (p.requireLogIn == this.isLoggedIn || p.requireLogOut != this.isLoggedIn);
  }


  public async createLoading(text=null, noPresent=false) {
    let loading = await this._loading.create({message: text});
    if (!noPresent) await loading.present();
    return loading;
  }

  public async successToast(loading=null, message=null) {
    if (loading) loading.dismiss();
    await (await this.toastCtrl.create({
      message: message||'Se ha completado la acción correctamente',
      duration: 3000,
      color: 'success'
    })).present();
  }

  public async errorToast(loading=null, message:any|string=null, duration=null) {
    if (loading) loading.dismiss();

    if (typeof message != 'string' && message!=null) {
      if (message.status && message.status==401 && this.isLoggedIn) {
        this.auth.logout(()=>this.navCtrl.navigateRoot('/login?msg=expired'));
      }
      message = this.fetchErrMsg(message.error);
    }

    return await (await this.toastCtrl.create({
      message: message||'Se ha producido un error, inténtelo de nuevo más tarde',
      duration: duration||5000,
      color: 'danger',
      buttons: [{text:'OK', role:'cancel'}]
    })).present();
  }
  public fetchErrMsg(err) {
    if (err.error) return err.error;
    if (err.errors) {
      let msg = 'Se han producido los siguientes errores: ';
      for (let itm in err.errors) {
        msg += '\n- '+err.errors[itm].toString();
      }
      return msg;
    }
    if (Object.getPrototypeOf(err).toString().indexOf('ProgressEvent')) return 'Se ha producido un error de comunicación, por favor, compruebe su conexión.';
  }

  public getDateFromTimestamp(date: string) {
    let t = date.split(/[- :]/);
    return new Date(Date.UTC(parseInt(t[0]), parseInt(t[1])-1, parseInt(t[2]), parseInt(t[3]), parseInt(t[4]), parseInt(t[5])));
  }

  public getIsoTime(date=null): string {
    const tzoffset = (new Date()).getTimezoneOffset() * 60000; //offset in milliseconds
    return (new Date(((date) ? date:Date.now()) - tzoffset)).toISOString().slice(0, -1);
  }

  public parseIsoTime(input: string): Date {
    let d = new Date(input.replace('Z', '')+'Z');
    d.setHours(d.getHours()+(d.getTimezoneOffset()/60));
    return d;
  }

  public copyToClipboard(e, selector = 'ion-input input') {
    e.target.parentNode.querySelector(selector).select();
    document.execCommand('copy');
  }

  fallbackCopyTextToClipboard(text) {
    var textArea = document.createElement("textarea");
    textArea.value = text;

    // Avoid scrolling to bottom
    textArea.style.top = "0";
    textArea.style.left = "0";
    textArea.style.position = "fixed";

    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      var successful = document.execCommand('copy');
      var msg = successful ? 'successful' : 'unsuccessful';
      // console.log('Fallback: Copying text command was ' + msg);
    } catch (err) {
      // console.error('Fallback: Oops, unable to copy', err);
    }

    document.body.removeChild(textArea);
  }


  platformReady() {
    this.platform.ready().then(p => {
      this.isBrowser = this.platform.is("desktop");
    });
  }
}
